// Import React polyfill FIRST to ensure React is globally available
import "./react-polyfill";

// src/main.tsx
import { createRoot } from "react-dom/client";
import React from "react";

// Comprehensive React availability check
if (typeof window !== "undefined") {
  (window as any).React = React;
  // Ensure Children API is specifically available
  if (!React.Children) {
    console.error("React.Children is not available!");
  }
}

import "./index.css";
import "swiper/swiper-bundle.css";
import "flatpickr/dist/flatpickr.css";
import 'sweetalert2/dist/sweetalert2.min.css';
import './styles/sweetalert-fix.css';

import "./styles/globals.css";
// import "./styles/layout.css";

import App from "./App.tsx";
import { AppWrapper } from "./components/common/PageMeta.tsx";
import { ThemeProvider } from "./context/ThemeContext.tsx";

// Optional ErrorBoundary wrapper for safer debugging
function ErrorBoundary({ children }: { children: React.ReactNode }) {
  const [err, setErr] = React.useState<any>(null);

  React.useEffect(() => {
    function onErr(e: any) {
      const value = e?.error ?? e;
      setErr(value);
      console.error("Global error:", value);
    }
    window.addEventListener("error", onErr);
    window.addEventListener("unhandledrejection", onErr);
    return () => {
      window.removeEventListener("error", onErr);
      window.removeEventListener("unhandledrejection", onErr);
    };
  }, []);

  if (err) {
    return (
      <div style={{ padding: 20 }}>
        <h2 style={{ color: "red" }}>App error</h2>
        <pre>{String(err?.message ?? err)}</pre>
        <details style={{ whiteSpace: "pre-wrap" }}>
          {String(err?.stack ?? "")}
        </details>
      </div>
    );
  }

  return <>{children}</>;
}

// Wait for DOM to be ready before initializing React app
function initializeApp() {
  createRoot(document.getElementById("root")!).render(
    <ThemeProvider>
      <AppWrapper>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </AppWrapper>
    </ThemeProvider>,
  );

  // Mark boot complete for white-screen diagnostics
}

// Initialize app when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeApp);
} else {
  initializeApp();
}
